import type { MicPreferences } from "@/lib/audio/micPreferences";
import { VoiceChanger } from "@/lib/audio/VoiceChanger";
import { voiceActive, voiceShape } from "@/lib/audio/voices";

export function micProcessingActive(preferences: MicPreferences): boolean {
  return (
    preferences.gain !== 1 ||
    preferences.clarity > 0 ||
    preferences.gate > 0 ||
    voiceActive(preferences)
  );
}

export function gateThreshold(gate: number): number {
  return 0.002 + gate * 0.026;
}

export function meterLevel(rms: number): number {
  if (rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  return Math.min(1, Math.max(0, (db + 60) / 60));
}

const GATE_INTERVAL_MS = 30;

const GATE_HOLD_MS = 300;

const GATE_ATTACK_S = 0.01;
const GATE_RELEASE_S = 0.25;

const GATE_HYSTERESIS = 0.7;

const PRESENCE_HZ = 2800;
const PRESENCE_MAX_DB = 8;

const RUMBLE_MIN_HZ = 60;
const RUMBLE_MAX_HZ = 120;

function audioContextClass(): typeof AudioContext | undefined {
  return (
    globalThis.AudioContext ??
    (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

export class MicProcessor {
  #context: AudioContext | null = null;
  #source: MediaStreamAudioSourceNode | null = null;
  #rumble: BiquadFilterNode | null = null;
  #gate: GainNode | null = null;
  #presence: BiquadFilterNode | null = null;
  #voice: VoiceChanger | null = null;
  #volume: GainNode | null = null;
  #monitor: GainNode | null = null;
  #monitoring = false;
  #destination: MediaStreamAudioDestinationNode | null = null;
  #analyser: AnalyserNode | null = null;
  #samples = new Float32Array(0);
  #timer: ReturnType<typeof setInterval> | null = null;

  #input: MediaStreamTrack | null = null;
  #output: MediaStreamTrack | null = null;
  #gate01 = 0;
  #openUntil = 0;
  #open = true;
  #level = 0;

  get output(): MediaStreamTrack | null {
    return this.#output;
  }

  start(track: MediaStreamTrack, preferences: MicPreferences): MediaStreamTrack {
    if (this.#input === track && this.#output !== null && this.#output.readyState === "live") {
      this.update(preferences);
      return this.#output;
    }

    this.stop();

    const Ctor = audioContextClass();
    if (Ctor === undefined) throw new Error("This browser has no Web Audio");

    const context = new Ctor();

    void context.resume().catch(() => undefined);

    const source = context.createMediaStreamSource(new MediaStream([track]));
    const rumble = context.createBiquadFilter();
    rumble.type = "highpass";
    rumble.Q.value = 0.7;

    const gate = context.createGain();
    const voice = new VoiceChanger(context);
    const presence = context.createBiquadFilter();
    presence.type = "highshelf";
    presence.frequency.value = PRESENCE_HZ;

    const volume = context.createGain();

    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -8;
    limiter.knee.value = 4;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.15;

    const destination = context.createMediaStreamDestination();

    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;

    const monitor = context.createGain();
    monitor.gain.value = 0;

    source.connect(rumble);
    rumble.connect(analyser);
    rumble.connect(gate);
    gate.connect(voice.input);
    voice.output.connect(presence);
    presence.connect(volume);
    volume.connect(limiter);
    limiter.connect(destination);
    limiter.connect(monitor);
    monitor.connect(context.destination);

    const [output] = destination.stream.getAudioTracks();
    if (output === undefined) throw new Error("The audio graph produced no track");

    this.#context = context;
    this.#source = source;
    this.#rumble = rumble;
    this.#gate = gate;
    this.#presence = presence;
    this.#voice = voice;
    this.#volume = volume;
    this.#monitor = monitor;
    this.#destination = destination;
    this.#analyser = analyser;
    this.#samples = new Float32Array(analyser.fftSize);
    this.#input = track;
    this.#output = output;
    this.#open = true;
    this.#openUntil = 0;

    this.update(preferences);
    this.#timer = setInterval(() => {
      this.#measure();
    }, GATE_INTERVAL_MS);

    return output;
  }

  update(preferences: MicPreferences): void {
    const context = this.#context;
    if (context === null) return;
    const at = context.currentTime;

    this.#volume?.gain.setTargetAtTime(preferences.gain, at, 0.02);
    this.#rumble?.frequency.setTargetAtTime(
      RUMBLE_MIN_HZ + preferences.clarity * (RUMBLE_MAX_HZ - RUMBLE_MIN_HZ),
      at,
      0.02,
    );
    this.#presence?.gain.setTargetAtTime(preferences.clarity * PRESENCE_MAX_DB, at, 0.02);
    this.#voice?.apply(voiceShape(preferences));

    const wasGated = this.#gate01 > 0;
    this.#gate01 = preferences.gate;

    if (wasGated && this.#gate01 === 0) this.#swing(true);
  }

  monitor(on: boolean): void {
    this.#monitoring = on;
    const monitor = this.#monitor;
    const context = this.#context;
    if (monitor === null || context === null) return;
    void context.resume().catch(() => undefined);
    monitor.gain.setTargetAtTime(on ? 1 : 0, context.currentTime, 0.03);
  }

  get monitoring(): boolean {
    return this.#monitoring;
  }

  stop(): void {
    if (this.#timer !== null) clearInterval(this.#timer);
    this.#timer = null;
    this.#source?.disconnect();
    this.#rumble?.disconnect();
    this.#gate?.disconnect();
    this.#voice?.stop();
    this.#presence?.disconnect();
    this.#volume?.disconnect();
    this.#monitor?.disconnect();
    this.#analyser?.disconnect();
    this.#destination?.disconnect();

    this.#output?.stop();
    const context = this.#context;
    this.#context = null;
    this.#source = null;
    this.#rumble = null;
    this.#gate = null;
    this.#voice = null;
    this.#presence = null;
    this.#volume = null;
    this.#monitor = null;
    this.#destination = null;
    this.#analyser = null;
    this.#input = null;
    this.#output = null;
    this.#level = 0;
    void context?.close().catch(() => undefined);
  }

  #measure(): void {
    const analyser = this.#analyser;
    if (analyser === null) return;
    analyser.getFloatTimeDomainData(this.#samples);
    let sum = 0;
    for (const sample of this.#samples) sum += sample * sample;
    this.#level = Math.sqrt(sum / this.#samples.length);

    if (this.#gate01 === 0) return;
    const threshold = gateThreshold(this.#gate01);
    const now = Date.now();
    if (this.#level >= threshold) {
      this.#openUntil = now + GATE_HOLD_MS;
      this.#swing(true);
      return;
    }

    if (this.#level >= threshold * GATE_HYSTERESIS || now < this.#openUntil) return;
    this.#swing(false);
  }

  #swing(open: boolean): void {
    const gate = this.#gate;
    const context = this.#context;
    if (gate === null || context === null || this.#open === open) return;
    this.#open = open;
    const at = context.currentTime;
    gate.gain.cancelScheduledValues(at);
    gate.gain.setValueAtTime(gate.gain.value, at);
    gate.gain.linearRampToValueAtTime(open ? 1 : 0, at + (open ? GATE_ATTACK_S : GATE_RELEASE_S));
  }
}
